import { jsPDF } from 'jspdf';
import { generateSpecification, HDF_BACK } from '@/lib/specification';
import { installCyrillicFont } from '@/lib/pdfFont';

// Параметры раскроя
const DEFAULTS = {
  trim: 10,   // обрезка краёв листа, мм
  kerf: 4,    // пропил, мм
  allowRotation: true,
};

// Shelf-packing First-Fit Decreasing Height с поворотом на 90°
function packSheet(panels, sheetL, sheetW, { trim, kerf, allowRotation }) {
  const placed = [];
  const remaining = [];
  let stripX = trim;
  let stripY = trim;
  let stripH = 0;

  for (const p of panels) {
    const orientations = allowRotation
      ? [{ w: p.L, h: p.W, rot: false }, { w: p.W, h: p.L, rot: true }]
      : [{ w: p.L, h: p.W, rot: false }];

    let placedPos = null;
    // Попытка 1: в текущую полосу
    for (const o of orientations) {
      const fitsX = stripX + o.w <= sheetL - trim;
      const maxStripH = Math.max(stripH, o.h);
      const fitsY = stripY + maxStripH <= sheetW - trim;
      if (fitsX && fitsY) {
        placedPos = { x: stripX, y: stripY, w: o.w, h: o.h, rot: o.rot };
        stripX += o.w + kerf;
        if (o.h > stripH) stripH = o.h;
        break;
      }
    }
    // Попытка 2: новая полоса
    if (!placedPos) {
      const newY = stripY + stripH + kerf;
      for (const o of orientations) {
        if (trim + o.w <= sheetL - trim && newY + o.h <= sheetW - trim) {
          stripX = trim;
          stripY = newY;
          stripH = o.h;
          placedPos = { x: stripX, y: stripY, w: o.w, h: o.h, rot: o.rot };
          stripX += o.w + kerf;
          break;
        }
      }
    }

    if (placedPos) placed.push({ ...p, ...placedPos });
    else remaining.push(p);
  }
  return { placed, remaining };
}

// Разворачивает qty в отдельные панели и упаковывает по листам
function packMaterialGroup(items, material, opts = DEFAULTS) {
  const panels = [];
  items.forEach((p, idx) => {
    for (let k = 0; k < p.qty; k++) {
      panels.push({ id: `${idx}.${k}`, name: p.name, L: p.L, W: p.W, edges: p.edges });
    }
  });
  panels.sort((a, b) => Math.max(b.L, b.W) - Math.max(a.L, a.W));

  const sheetL = material.sheetL || 2750;
  const sheetW = material.sheetW || 1830;
  const sheets = [];
  let remaining = panels;
  let safety = 0;
  while (remaining.length > 0 && safety < 50) {
    const { placed, remaining: next } = packSheet(remaining, sheetL, sheetW, opts);
    if (placed.length === 0) {
      // Защита от бесконечного цикла: деталь больше листа
      break;
    }
    sheets.push({ panels: placed, sheetL, sheetW });
    remaining = next;
    safety++;
  }
  return { sheets, unplaced: remaining };
}

export function generateNesting(modules, opts = DEFAULTS) {
  const spec = generateSpecification(modules);
  const groups = spec.materials.map(mg => {
    const { sheets, unplaced } = packMaterialGroup(mg.items, mg.material, opts);
    const usedArea = sheets.reduce((s, sh) => s + sh.panels.reduce((ss, p) => ss + p.w * p.h, 0), 0);
    const sheetArea = sheets.length * (mg.material.sheetL || 0) * (mg.material.sheetW || 0);
    return {
      material: mg.material,
      colorId: mg.colorId,
      sheets,
      unplaced,
      totalSheets: sheets.length,
      utilization: sheetArea > 0 ? usedArea / sheetArea : 0,
    };
  });
  return groups;
}

// ---------- PDF ----------

function materialTitle(g) {
  if (g.material === HDF_BACK) return `${HDF_BACK.nameRu}, ${HDF_BACK.color}`;
  const colorName = g.colorId ? (g.material.colorName || g.colorId) : '';
  return `${g.material.nameRu}${colorName ? ', ' + colorName : ''}`;
}

// Переводит мм детали в мм PDF-холста через scale + offset
function drawSheet(pdf, group, sheetIdx, sheet, projectName) {
  const pageW = pdf.internal.pageSize.getWidth();   // 297
  const pageH = pdf.internal.pageSize.getHeight();  // 210
  const margin = 12;
  const headerH = 22;
  const footerH = 8;
  const drawW = pageW - 2 * margin;
  const drawH = pageH - margin - headerH - footerH;

  const scale = Math.min(drawW / sheet.sheetL, drawH / sheet.sheetW);
  const offsetX = margin + (drawW - sheet.sheetL * scale) / 2;
  const offsetY = margin + headerH + (drawH - sheet.sheetW * scale) / 2;

  // Шапка
  pdf.setFont('Roboto', 'bold'); pdf.setFontSize(11);
  pdf.text(`${projectName} · Карта раскроя`, margin, margin + 5);
  pdf.setFont('Roboto', 'normal'); pdf.setFontSize(9);
  pdf.text(materialTitle(group), margin, margin + 11);
  pdf.text(
    `Лист ${sheetIdx + 1} из ${group.totalSheets} · ${sheet.sheetL}×${sheet.sheetW} мм · деталей: ${sheet.panels.length}`,
    margin, margin + 17,
  );

  // Контур листа
  pdf.setLineWidth(0.4);
  pdf.setDrawColor(0);
  pdf.rect(offsetX, offsetY, sheet.sheetL * scale, sheet.sheetW * scale);

  // Рамка чистого поля (после обрезки)
  pdf.setLineWidth(0.1);
  pdf.setDrawColor(180);
  pdf.rect(
    offsetX + DEFAULTS.trim * scale,
    offsetY + DEFAULTS.trim * scale,
    (sheet.sheetL - 2 * DEFAULTS.trim) * scale,
    (sheet.sheetW - 2 * DEFAULTS.trim) * scale,
  );

  // Детали
  pdf.setLineWidth(0.25);
  pdf.setDrawColor(30);
  pdf.setFontSize(6.5);
  sheet.panels.forEach((p, i) => {
    const x = offsetX + p.x * scale;
    const y = offsetY + p.y * scale;
    const w = p.w * scale;
    const h = p.h * scale;
    // Заливка светлым фоном
    pdf.setFillColor(244, 241, 233);
    pdf.rect(x, y, w, h, 'FD');
    // Подпись: № и L×W
    const labelId = `${i + 1}`;
    const labelDims = `${p.L}×${p.W}${p.rot ? ' ⟲' : ''}`;
    const labelName = p.name;
    const cx = x + w / 2;
    const cy = y + h / 2;
    if (w > 18 && h > 10) {
      pdf.setTextColor(60);
      pdf.text(labelId, x + 1.5, y + 3);
      pdf.setTextColor(20);
      pdf.text(labelName, cx, cy - 1, { align: 'center' });
      pdf.text(labelDims, cx, cy + 3, { align: 'center' });
    } else if (w > 10) {
      pdf.text(labelId, cx, cy + 1, { align: 'center' });
    }
  });

  // Шкала
  pdf.setFontSize(7);
  pdf.setTextColor(100);
  pdf.text(`Масштаб 1:${Math.round(1 / scale)} · обрезка ${DEFAULTS.trim} мм · пропил ${DEFAULTS.kerf} мм`,
    margin, pageH - 4);
  pdf.setTextColor(0);
}

export async function exportNestingPDF(modules, { projectName = 'Проект' } = {}) {
  const groups = generateNesting(modules);
  if (groups.length === 0 || groups.every(g => g.totalSheets === 0)) {
    return { groups, sheetsCount: 0 };
  }

  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  await installCyrillicFont(pdf);

  // Титульная страница со сводкой
  pdf.setFont('Roboto', 'bold'); pdf.setFontSize(16);
  pdf.text(`${projectName}`, 12, 18);
  pdf.setFontSize(12);
  pdf.text('Карта раскроя', 12, 26);
  pdf.setFont('Roboto', 'normal'); pdf.setFontSize(10);
  let y = 40;
  pdf.text('Материал', 12, y);
  pdf.text('Лист', 100, y);
  pdf.text('Листов', 160, y);
  pdf.text('Деталей', 195, y);
  pdf.text('Использовано', 230, y);
  pdf.setLineWidth(0.2);
  pdf.line(12, y + 1.5, 285, y + 1.5);
  y += 7;
  let grandSheets = 0;
  let grandPanels = 0;
  groups.forEach(g => {
    const panels = g.sheets.reduce((s, sh) => s + sh.panels.length, 0) + g.unplaced.length;
    grandSheets += g.totalSheets;
    grandPanels += panels;
    pdf.text(materialTitle(g), 12, y);
    pdf.text(`${g.material.sheetL}×${g.material.sheetW}`, 100, y);
    pdf.text(String(g.totalSheets), 160, y);
    pdf.text(String(panels), 195, y);
    pdf.text(`${(g.utilization * 100).toFixed(1)}%`, 230, y);
    y += 6;
    if (g.unplaced.length > 0) {
      pdf.setTextColor(200, 50, 30);
      pdf.text(`⚠ не размещено: ${g.unplaced.length}`, 20, y);
      pdf.setTextColor(0);
      y += 6;
    }
  });
  pdf.line(12, y, 285, y);
  y += 6;
  pdf.setFont('Roboto', 'bold');
  pdf.text(`Итого: ${grandSheets} лист(ов), ${grandPanels} деталей`, 12, y);
  pdf.setFont('Roboto', 'normal');

  // Листы раскроя
  groups.forEach(g => {
    g.sheets.forEach((sheet, idx) => {
      pdf.addPage();
      drawSheet(pdf, g, idx, sheet, projectName);
    });
  });

  const safeName = (projectName || 'nesting').replace(/[^\wа-яА-Я0-9-_ ]/gi, '').slice(0, 60);
  pdf.save(`${safeName || 'nesting'}_раскрой.pdf`);
  return { groups, sheetsCount: grandSheets };
}
